import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLightbulb,
  faRocket,
  faExclamationTriangle,
  faCheckCircle,
  faTimesCircle,
  faClock,
  faSpinner,
  faPlay,
  faEye,
  faSync,
  faDollarSign,
  faWrench,
  faServer,
  faDatabase,
  faBug,
  faTimes,
  faChevronDown,
  faChevronUp
} from '@fortawesome/free-solid-svg-icons';
import { faAws, faGoogle, faGithub } from '@fortawesome/free-brands-svg-icons';
import {
  Recommendation,
  RecommendationSummary,
  RecommendationFilters
} from '../types';
import { recommendationService } from '../services/recommendationService';
import { intelligentRecommendationService } from '../services/intelligentRecommendationService';
import { CostData } from '../types';

interface RecommendationsPanelV1Props {
  onDeployRecommendation: (recommendationId: string) => void;
  loading?: boolean;
  costData?: CostData | null;
  externalScanTrigger?: number;
  onScanComplete?: () => void;
  onScanProgress?: (message: string) => void;
}

// Helper functions
const getPriorityColor = (priority: 'high' | 'medium' | 'low') => {
  switch (priority) {
    case 'high': return 'bg-red-100 text-red-800 border-red-200';
    case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'low': return 'bg-green-100 text-green-800 border-green-200';
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'pending': return 'bg-gray-100 text-gray-800';
    case 'approved': return 'bg-blue-100 text-blue-800';
    case 'in_progress': return 'bg-yellow-100 text-yellow-800';
    case 'completed': return 'bg-green-100 text-green-800';
    case 'rejected': return 'bg-red-100 text-red-800';
    case 'failed': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'pending': return faClock;
    case 'approved': return faCheckCircle;
    case 'in_progress': return faSpinner;
    case 'completed': return faCheckCircle;
    case 'rejected': return faTimesCircle;
    case 'failed': return faExclamationTriangle;
    default: return faClock;
  }
};

const getCloudIcon = (cloud: 'aws' | 'gcp' | 'github') => {
  switch (cloud) {
    case 'aws': return faAws;
    case 'gcp': return faGoogle;
    case 'github': return faGithub;
  }
};

const getCategoryIcon = (category: string) => {
  switch (category) {
    case 'compute_optimization': return faServer;
    case 'storage_optimization': return faDatabase;
    case 'resource_cleanup': return faWrench;
    case 'network_optimization': return faWrench;
    case 'database_optimization': return faDatabase;
    case 'savings_plan': return faDollarSign;
    case 'reserved_instances': return faDollarSign;
    case 'cost_savings': return faDollarSign;
    default: return faLightbulb;
  }
};

interface RecommendationCardProps {
  recommendation: Recommendation;
  onDeploy: (id: string) => void;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  onViewDetails: (id: string) => void;
}

const RecommendationCard: React.FC<RecommendationCardProps> = ({
  recommendation,
  onDeploy,
  onApprove,
  onReject,
  onViewDetails
}) => {
  const canDeploy = recommendation.status === 'approved';
  const canApprove = recommendation.status === 'pending';

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="space-y-3">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
              recommendation.cloud === 'aws' ? 'bg-orange-100' :
              recommendation.cloud === 'gcp' ? 'bg-blue-100' : 'bg-gray-100'
            }`}>
              <FontAwesomeIcon
                icon={getCloudIcon(recommendation.cloud)}
                className={`text-xs ${
                  recommendation.cloud === 'aws' ? 'text-orange-600' :
                  recommendation.cloud === 'gcp' ? 'text-blue-600' : 'text-gray-600'
                }`}
              />
            </div>
            <div className="flex-1">
              <div className="flex items-center space-x-2">
                <h4 className="text-xs font-semibold text-gray-900">{recommendation.title}</h4>
                <FontAwesomeIcon icon={getCategoryIcon(recommendation.category)} className="text-gray-400 text-xs" />
              </div>
              <p className="text-xs text-gray-600 mt-1">{recommendation.description}</p>
            </div>
          </div>

          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(recommendation.priority)}`}>
            {recommendation.priority.toUpperCase()}
          </span>
        </div>

        {/* Resource Details */}
        <div className="bg-gray-50 rounded-lg p-3 text-xs">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <span className="font-medium text-gray-700">Resource: </span>
              <span className="text-gray-600">{recommendation.resourceDetails.resourceName}</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Type: </span>
              <span className="text-gray-600">{recommendation.resourceDetails.resourceType}</span>
            </div>
            {recommendation.resourceDetails.region && (
              <div>
                <span className="font-medium text-gray-700">Region: </span>
                <span className="text-gray-600">{recommendation.resourceDetails.region}</span>
              </div>
            )}
            {recommendation.resourceDetails.metrics && (
              <div>
                <span className="font-medium text-gray-700">Utilization: </span>
                <span className="text-gray-600">{recommendation.resourceDetails.metrics.utilizationRate}%</span>
              </div>
            )}
          </div>
        </div>

        {/* Savings & Status */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="text-xs">
              <span className="font-semibold text-green-600">
                ${recommendation.impact.monthlySavings.toLocaleString()}/mo
              </span>
              <span className="text-gray-500 text-xs ml-1">
                (${recommendation.impact.annualSavings.toLocaleString()}/yr)
              </span>
            </div>
            <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(recommendation.status)}`}>
              <FontAwesomeIcon
                icon={getStatusIcon(recommendation.status)}
                className={`mr-1 text-xs ${recommendation.status === 'in_progress' ? 'animate-spin' : ''}`}
              />
              {recommendation.status.replace('_', ' ').toUpperCase()}
            </div>
          </div>

          <div className="text-xs text-gray-500">
            Effort: {recommendation.effort}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pt-2 border-t border-gray-200">
          <button
            onClick={() => onViewDetails(recommendation.id)}
            className="text-xs text-gray-600 hover:text-gray-800 transition-colors"
          >
            <FontAwesomeIcon icon={faEye} className="mr-1" />
            View Details
          </button>

          <div className="flex items-center space-x-2">
            {canApprove && (
              <>
                <button
                  onClick={() => onReject(recommendation.id)}
                  className="px-3 py-1 text-xs bg-red-50 text-red-600 border border-red-200 rounded hover:bg-red-100 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimesCircle} className="mr-1" />
                  Reject
                </button>
                <button
                  onClick={() => onApprove(recommendation.id)}
                  className="px-3 py-1 text-xs bg-green-50 text-green-600 border border-green-200 rounded hover:bg-green-100 transition-colors"
                >
                  <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
                  Approve
                </button>
              </>
            )}

            {canDeploy && (
              <button
                onClick={() => onDeploy(recommendation.id)}
                className="px-3 py-1 text-xs bg-blue-50 text-blue-600 border border-blue-200 rounded hover:bg-blue-100 transition-colors"
              >
                <FontAwesomeIcon icon={faPlay} className="mr-1" />
                Deploy
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const RecommendationsPanelV1: React.FC<RecommendationsPanelV1Props> = ({
  onDeployRecommendation,
  loading = false,
  costData = null,
  externalScanTrigger,
  onScanComplete,
  onScanProgress
}) => {
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [summary, setSummary] = useState<RecommendationSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scanStatus, setScanStatus] = useState<any>(null);
  const [isScanningAWS, setIsScanningAWS] = useState(false);
  const [setupMessage, setSetupMessage] = useState<string | null>(null);
  const [showDebugModal, setShowDebugModal] = useState(false);
  const [scanDetails, setScanDetails] = useState<any>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedRecommendation, setSelectedRecommendation] = useState<Recommendation | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchRecommendations = async (appliedFilters?: RecommendationFilters) => {
    try {
      setIsLoading(true);
      setError(null);
      setSetupMessage(null);

      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
      const token = localStorage.getItem('auth_token');

      const params = new URLSearchParams();
      if (appliedFilters?.cloud?.length) params.append('clouds', appliedFilters.cloud.join(','));
      if (appliedFilters?.category?.length) params.append('categories', appliedFilters.category.join(','));
      if (appliedFilters?.priority?.length) params.append('priorities', appliedFilters.priority.join(','));
      if (appliedFilters?.status?.length) params.append('statuses', appliedFilters.status.join(','));
      const qs = params.toString();

      console.log('[V1] Fetching recommendations from API...');
      const res = await fetch(`${baseUrl}/api/cost/recommendations${qs ? `?${qs}` : ''}`, {
        headers: { ...(token && { 'Authorization': `Bearer ${token}` }) }
      });
      const data = await res.json();
      console.log('[V1] API Response:', data);

      if (data.requiresSetup) {
        setSetupMessage(data.message || 'Connect an AWS account to get started.');
        setRecommendations([]);
        setSummary({
          total: 0,
          byPriority: { high: 0, medium: 0, low: 0 },
          byStatus: { pending: 0, approved: 0, in_progress: 0, completed: 0, rejected: 0, failed: 0 },
          totalPotentialSavings: { monthly: 0, annual: 0, currency: 'USD' },
          byCloud: { aws: 0, gcp: 0, github: 0 }
        });
        return;
      }

      if (data.requiresScan) {
        // No scan has been run yet — show empty state with Scan AWS button
        setRecommendations([]);
        setSummary(null);
        return;
      }

      const recs: Recommendation[] = data.data || [];
      setRecommendations(recs);
      const generatedSummary = intelligentRecommendationService.generateIntelligentSummary(recs);
      setSummary(generatedSummary);
    } catch (err: any) {
      console.error('[V1] Error fetching recommendations:', err);
      setError(err.message);
      setRecommendations([]);
      setSummary({
        total: 0,
        byPriority: { high: 0, medium: 0, low: 0 },
        byStatus: { pending: 0, approved: 0, in_progress: 0, completed: 0, rejected: 0, failed: 0 },
        totalPotentialSavings: { monthly: 0, annual: 0, currency: 'USD' },
        byCloud: { aws: 0, gcp: 0, github: 0 }
      });
    } finally {
      setIsLoading(false);
    }
  };

  const generateRecommendations = async () => {
    try {
      setIsLoading(true);
      setError(null);
      console.log('[V1] Generating AI-powered recommendations...');
      await recommendationService.generateRecommendations(true);
      await fetchRecommendations();
    } catch (err: any) {
      console.error('[V1] Error generating recommendations:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const startAWSScan = async () => {
    try {
      // Clear any existing poll interval before starting a new scan
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      setIsScanningAWS(true);
      setIsLoading(true);
      setRecommendations([]);
      setError(null);
      setSetupMessage(null);

      console.log('[V1] Starting AWS resource scan...');
      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
      const token = localStorage.getItem('auth_token');

      const response = await fetch(`${baseUrl}/api/cost/scan/aws`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({})
      });

      const result = await response.json();

      if (!result.success) {
        if (result.errorCode === 'NO_AWS_ACCOUNT' || result.errorCode === 'MISSING_IAM_CONFIG') {
          setSetupMessage(result.error);
        } else {
          setError(result.error || 'Failed to start AWS scan');
        }
        setIsScanningAWS(false);
        setIsLoading(false);
        return;
      }

      const scanId = result.scanId || result.data?.scanId;
      if (scanId) {
        setScanStatus({
          scanId: scanId,
          status: result.status || result.data?.status,
          message: result.message || result.data?.message
        });
        pollScanStatus(scanId);
      } else {
        console.error('[V1] No scanId in response:', result);
        setError('Scan failed: no scanId returned');
        setIsScanningAWS(false);
        setIsLoading(false);
      }
    } catch (err: any) {
      console.error('[V1] Error starting AWS scan:', err);
      setError('Failed to start AWS scan: ' + err.message);
      setIsScanningAWS(false);
      setIsLoading(false);
    }
  };

  const fetchScanDetails = async (scanId: string) => {
    try {
      setLoadingDetails(true);
      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
      const token = localStorage.getItem('auth_token');

      const response = await fetch(`${baseUrl}/api/cost/scan/aws/${scanId}/details`, {
        headers: { ...(token && { 'Authorization': `Bearer ${token}` }) }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch scan details: ${response.statusText}`);
      }

      const result = await response.json();
      if (result.success) {
        setScanDetails(result);
        setShowDebugModal(true);
      } else {
        setError(result.error || 'Failed to load scan details');
      }
    } catch (err: any) {
      console.error('[V1] Error fetching scan details:', err);
      setError('Failed to load scan details: ' + err.message);
    } finally {
      setLoadingDetails(false);
    }
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const pollScanStatus = async (scanId: string) => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
    const token = localStorage.getItem('auth_token');

    const playQueue: string[] = [
      'Starting AWS scan...',
      'Connecting to your AWS account & setting up scan...'
    ];
    let currentPlayIndex = 0;
    let backendFinished = false;
    let backendFailed = false;
    let finalError = '';

    onScanProgress?.(playQueue[0]);

    const playInterval = setInterval(() => {
      if (currentPlayIndex < playQueue.length - 1) {
        currentPlayIndex++;
        const msg = playQueue[currentPlayIndex];
        setScanStatus((prev: any) => ({ ...prev, currentTask: msg }));
        onScanProgress?.(msg);
      } else if (backendFinished) {
        clearInterval(playInterval);
        setIsScanningAWS(false);
        console.log('[V1] Scan completed, refreshing recommendations...');
        setTimeout(async () => { await fetchRecommendations(); }, 1000);
        onScanComplete?.();
      } else if (backendFailed) {
        clearInterval(playInterval);
        setIsScanningAWS(false);
        const errMsg = finalError || 'Scan failed';
        setScanStatus((prev: any) => ({ ...prev, status: 'failed', currentTask: errMsg }));
        onScanProgress?.(errMsg);
      }
    }, 800);

    pollIntervalRef.current = setInterval(async () => {
      try {
        const response = await fetch(`${baseUrl}/api/cost/scan-status/${scanId}`, {
          headers: { ...(token && { 'Authorization': `Bearer ${token}` }) }
        });

        if (!response.ok) {
          clearInterval(pollIntervalRef.current!);
          pollIntervalRef.current = null;
          clearInterval(playInterval);
          setIsScanningAWS(false);
          return;
        }

        const result = await response.json();

        if (result.success && result.data) {
          // Append new tasks from messageQueue
          const newTasks = result.data.messageQueue || [];
          if (result.data.currentTask && !newTasks.includes(result.data.currentTask)) {
            newTasks.push(result.data.currentTask);
          }
          newTasks.forEach((msg: any) => {
            if (msg && typeof msg === 'string' && !playQueue.includes(msg)) {
              if (msg.includes('Identifying idle')) return; // skip identifying idle & wasteful message
              playQueue.push(msg);
            }
          });

          setScanStatus((prev: any) => ({
            ...prev,
            ...result.data,
            scanId,
            currentTask: playQueue[currentPlayIndex] // keep using current playback task
          }));

          if (result.data.status === 'completed') {
            clearInterval(pollIntervalRef.current!);
            pollIntervalRef.current = null;
            backendFinished = true;
          } else if (result.data.status === 'failed') {
            clearInterval(pollIntervalRef.current!);
            pollIntervalRef.current = null;
            backendFailed = true;
            finalError = result.data.error;
          }
        }
      } catch (err) {
        console.error('[V1] Error polling scan status:', err);
        clearInterval(pollIntervalRef.current!);
        pollIntervalRef.current = null;
        clearInterval(playInterval);
        setIsScanningAWS(false);
      }
    }, 5000);

    setTimeout(() => {
      if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }
      clearInterval(playInterval);
      setIsScanningAWS(false);
    }, 15 * 60 * 1000);
  };

  const handleApprove = async (recommendationId: string) => {
    try {
      const recommendation = recommendations.find(rec => rec.id === recommendationId);
      if (!recommendation) return;

      setRecommendations(prev =>
        prev.map(rec => rec.id === recommendationId ? { ...rec, status: 'approved' as const } : rec)
      );

      const mapToSavingsCategory = (recCategory: string, title: string): string => {
        const titleLower = title.toLowerCase();
        const categoryLower = recCategory.toLowerCase();
        if (titleLower.includes('arm') || titleLower.includes('graviton')) return 'x86ToArm';
        if (titleLower.includes('savings plan') || categoryLower.includes('savings')) return 'savingsPlans';
        if (titleLower.includes('reserved') || titleLower.includes('ri ')) return 'reservedInstances';
        if (titleLower.includes('spot')) return 'spotInstances';
        if (titleLower.includes('rightsize') || titleLower.includes('right-size') || titleLower.includes('downsize')) return 'rightsizing';
        if (categoryLower.includes('storage') || titleLower.includes('storage') || titleLower.includes('ebs') || titleLower.includes('s3')) return 'storageOptimization';
        if (titleLower.includes('schedule') || titleLower.includes('shutdown') || titleLower.includes('stop')) return 'scheduling';
        if (titleLower.includes('devopsx') || titleLower.includes('discount')) return 'preApprovedDiscounts';
        if (titleLower.includes('credit')) return 'credits';
        return 'other';
      };

      const category = mapToSavingsCategory(recommendation.category, recommendation.title);
      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
      const token = localStorage.getItem('auth_token');

      const response = await fetch(`${baseUrl}/api/savings/record-achievement`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          recommendationId: recommendation.id,
          monthlySavings: recommendation.impact.monthlySavings,
          category,
          description: recommendation.title
        })
      });

      const result = await response.json();
      if (result.success) {
        console.log('[V1] Savings recorded:', result.data);
      } else {
        console.warn('[V1] Failed to record savings:', result.error);
      }
    } catch (err: any) {
      console.error('[V1] Error approving recommendation:', err);
      setError(err.message);
    }
  };

  const handleReject = async (recommendationId: string) => {
    try {
      setRecommendations(prev =>
        prev.map(rec => rec.id === recommendationId ? { ...rec, status: 'rejected' as const } : rec)
      );
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleViewDetails = (recommendationId: string) => {
    const recommendation = recommendations.find(rec => rec.id === recommendationId);
    if (recommendation) {
      setSelectedRecommendation(recommendation);
      setShowDetailsModal(true);
    }
  };

  useEffect(() => {
    fetchRecommendations();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const prevTriggerRef = useRef<number>(externalScanTrigger ?? 0);
  useEffect(() => {
    if (!externalScanTrigger || externalScanTrigger === prevTriggerRef.current) return;
    prevTriggerRef.current = externalScanTrigger;
    startAWSScan();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalScanTrigger]);

  return (
    <div className="space-y-4">

      {/* Scan Completed Banner */}
      {!isScanningAWS && scanStatus?.scanId && scanStatus?.status === 'completed' && (
        <div className="bg-green-50 border border-green-300 rounded-lg p-3 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <FontAwesomeIcon icon={faCheckCircle} className="text-green-600" />
            <span className="text-xs font-semibold text-green-900">AWS scan completed successfully!</span>
          </div>
          <button
            onClick={() => fetchScanDetails(scanStatus.scanId)}
            className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700 transition-colors flex items-center"
          >
            <FontAwesomeIcon icon={faBug} className="mr-1" />
            View Scan Report
          </button>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600" />
            <div>
              <div className="text-xs font-semibold text-red-900">Failed to load recommendations</div>
              <div className="text-xs text-red-700 mt-1">{error}</div>
            </div>
            <button
              onClick={() => fetchRecommendations()}
              className="ml-auto px-3 py-1.5 bg-red-100 border border-red-200 text-red-700 rounded-lg text-xs font-medium hover:bg-red-200"
            >
              <FontAwesomeIcon icon={faSync} className="mr-1" />
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-4 animate-pulse">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
                  <div>
                    <div className="h-4 bg-gray-200 rounded w-48 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-64"></div>
                  </div>
                </div>
                <div className="w-16 h-6 bg-gray-200 rounded-full"></div>
              </div>
              <div className="h-16 bg-gray-100 rounded-lg mb-3"></div>
              <div className="flex items-center justify-between">
                <div className="h-4 bg-gray-200 rounded w-24"></div>
                <div className="flex space-x-2">
                  <div className="h-6 bg-gray-200 rounded w-16"></div>
                  <div className="h-6 bg-gray-200 rounded w-16"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Recommendations List — hide during active scan to prevent race condition flash */}
      {!isLoading && !isScanningAWS && recommendations.length > 0 && (
        <div className="space-y-3">
          {recommendations.map((recommendation) => (
            <RecommendationCard
              key={recommendation.id}
              recommendation={recommendation}
              onDeploy={onDeployRecommendation}
              onApprove={handleApprove}
              onReject={handleReject}
              onViewDetails={handleViewDetails}
            />
          ))}
        </div>
      )}

      {/* Setup Required */}
      {setupMessage && !isLoading && (
        <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-6 text-center">
          <FontAwesomeIcon icon={faServer} className="text-blue-600 text-3xl mb-3" />
          <div className="text-xs font-semibold text-blue-900 mb-2">AWS Account Setup Required</div>
          <div className="text-xs text-blue-700 mb-4 max-w-lg mx-auto">{setupMessage}</div>
          <button
            onClick={() => window.location.href = '/settings'}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs"
          >
            Go to Settings
          </button>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !isScanningAWS && recommendations.length === 0 && !error && !setupMessage && (
        <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
          <FontAwesomeIcon icon={faLightbulb} className="text-gray-400 text-3xl mb-3" />
          <div className="text-xs font-medium text-gray-900 mb-1">No recommendations available</div>
          <div className="text-xs text-gray-500 mb-4">
            Click "Scan AWS" to analyze your infrastructure and generate recommendations.
          </div>
          <button
            onClick={startAWSScan}
            disabled={isScanningAWS}
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 transition-colors text-xs"
          >
            <FontAwesomeIcon icon={faServer} className="mr-2" />
            Scan AWS Resources
          </button>
        </div>
      )}

      {/* Recommendation Details Modal */}
      {showDetailsModal && selectedRecommendation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  selectedRecommendation.cloud === 'aws' ? 'bg-orange-100' :
                  selectedRecommendation.cloud === 'gcp' ? 'bg-blue-100' : 'bg-gray-100'
                }`}>
                  <FontAwesomeIcon
                    icon={getCloudIcon(selectedRecommendation.cloud)}
                    className={`text-sm ${
                      selectedRecommendation.cloud === 'aws' ? 'text-orange-600' :
                      selectedRecommendation.cloud === 'gcp' ? 'text-blue-600' : 'text-gray-600'
                    }`}
                  />
                </div>
                <div>
                  <h2 className="text-xs font-bold text-white">{selectedRecommendation.title}</h2>
                  <p className="text-xs text-blue-100">Recommendation Details & Implementation Guide</p>
                </div>
              </div>
              <button onClick={() => setShowDetailsModal(false)} className="text-white hover:text-blue-200">
                <FontAwesomeIcon icon={faTimes} className="text-xl" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              <div className="flex items-center justify-between">
                <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium border ${getPriorityColor(selectedRecommendation.priority)}`}>
                  {selectedRecommendation.priority.toUpperCase()} PRIORITY
                </span>
                <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium ${getStatusColor(selectedRecommendation.status)}`}>
                  <FontAwesomeIcon
                    icon={getStatusIcon(selectedRecommendation.status)}
                    className={`mr-1.5 ${selectedRecommendation.status === 'in_progress' ? 'animate-spin' : ''}`}
                  />
                  {selectedRecommendation.status.replace('_', ' ').toUpperCase()}
                </span>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-xs font-semibold text-blue-900 mb-2 flex items-center">
                  <FontAwesomeIcon icon={faLightbulb} className="mr-2" />
                  Why This Recommendation?
                </h3>
                <p className="text-xs text-gray-700 whitespace-pre-wrap">{selectedRecommendation.description}</p>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="text-xs font-semibold text-green-900 mb-3 flex items-center">
                  <FontAwesomeIcon icon={faDollarSign} className="mr-2" />
                  Estimated Cost Savings
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-gray-600 mb-1">Monthly Savings</div>
                    <div className="text-3xl font-bold text-green-700">
                      ${selectedRecommendation.impact.monthlySavings.toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">per month</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-600 mb-1">Annual Savings</div>
                    <div className="text-3xl font-bold text-green-700">
                      ${selectedRecommendation.impact.annualSavings.toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">per year</div>
                  </div>
                </div>
                {(selectedRecommendation.impact as any).savingsPercent && (
                  <div className="mt-3 pt-3 border-t border-green-200">
                    <span className="text-xs text-green-800">
                      Savings: <span className="font-bold">{(selectedRecommendation.impact as any).savingsPercent}%</span> reduction in costs
                    </span>
                  </div>
                )}
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="text-xs font-semibold text-gray-900 mb-3 flex items-center">
                  <FontAwesomeIcon icon={faServer} className="mr-2" />
                  Resource Information
                </h3>
                <div className="space-y-2 text-xs">
                  <div className="flex items-start">
                    <span className="font-medium text-gray-700 w-32">Resource Name:</span>
                    <span className="text-gray-900 font-mono">{selectedRecommendation.resourceDetails.resourceName}</span>
                  </div>
                  <div className="flex items-start">
                    <span className="font-medium text-gray-700 w-32">Resource Type:</span>
                    <span className="text-gray-900">{selectedRecommendation.resourceDetails.resourceType}</span>
                  </div>
                  {selectedRecommendation.resourceDetails.region && (
                    <div className="flex items-start">
                      <span className="font-medium text-gray-700 w-32">Region:</span>
                      <span className="text-gray-900">{selectedRecommendation.resourceDetails.region}</span>
                    </div>
                  )}
                  {selectedRecommendation.resourceDetails.accountId && (
                    <div className="flex items-start">
                      <span className="font-medium text-gray-700 w-32">Account ID:</span>
                      <span className="text-gray-900 font-mono">{selectedRecommendation.resourceDetails.accountId}</span>
                    </div>
                  )}
                  {(selectedRecommendation.resourceDetails as any).currentConfig && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <span className="font-medium text-gray-700 block mb-2">Current Configuration:</span>
                      <div className="bg-white rounded p-3 border border-gray-200">
                        <pre className="text-xs text-gray-700 whitespace-pre-wrap">
                          {JSON.stringify((selectedRecommendation.resourceDetails as any).currentConfig, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}
                  {(selectedRecommendation.resourceDetails as any).recommendedConfig && (
                    <div className="mt-3">
                      <span className="font-medium text-gray-700 block mb-2">Recommended Configuration:</span>
                      <div className="bg-green-50 rounded p-3 border border-green-200">
                        <pre className="text-xs text-gray-700 whitespace-pre-wrap">
                          {JSON.stringify((selectedRecommendation.resourceDetails as any).recommendedConfig, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {(selectedRecommendation as any).actions && (selectedRecommendation as any).actions.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h3 className="text-xs font-semibold text-yellow-900 mb-3 flex items-center">
                    <FontAwesomeIcon icon={faWrench} className="mr-2" />
                    Implementation Steps
                  </h3>
                  <div className="space-y-3">
                    {(selectedRecommendation as any).actions.map((action: any, index: number) => (
                      <div key={action.id} className="bg-white rounded-lg p-3 border border-yellow-200">
                        <div className="flex items-start space-x-3">
                          <div className="w-6 h-6 rounded-full bg-yellow-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                            {index + 1}
                          </div>
                          <div className="flex-1">
                            <div className="text-xs font-medium text-gray-900 mb-1">{action.description}</div>
                            <div className="flex items-center space-x-4 text-xs text-gray-600">
                              <span>Duration: {action.estimatedDuration}</span>
                              <span>Risk: <span className={`font-medium ${
                                action.riskLevel === 'high' ? 'text-red-600' :
                                action.riskLevel === 'medium' ? 'text-yellow-600' : 'text-green-600'
                              }`}>{action.riskLevel}</span></span>
                              <span>Rollback: {action.rollbackPossible ? '✓ Yes' : '✗ No'}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 text-xs text-yellow-800">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
                    Total estimated time: {(selectedRecommendation as any).actions.reduce((sum: number, action: any) => {
                      const match = action.estimatedDuration.match(/(\d+)/);
                      return sum + (match ? parseInt(match[1]) : 0);
                    }, 0)} minutes
                  </div>
                </div>
              )}

              {(selectedRecommendation as any).awsCommands && (selectedRecommendation as any).awsCommands.length > 0 && (
                <div className="bg-gray-900 border border-gray-700 rounded-lg p-4">
                  <h3 className="text-xs font-semibold text-white mb-3 flex items-center">
                    <FontAwesomeIcon icon={faRocket} className="mr-2 text-orange-400" />
                    AWS CLI Commands
                  </h3>
                  <div className="bg-gray-800 rounded p-3 font-mono text-xs text-green-400 overflow-x-auto">
                    {(selectedRecommendation as any).awsCommands.map((cmd: string, index: number) => (
                      <div key={index} className="py-1">
                        <span className="text-gray-500">$ </span>{cmd}
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 text-xs text-gray-400">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1 text-yellow-500" />
                    Review commands carefully before execution. Always test in non-production first.
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <div className="text-xs text-gray-600 mb-1">Implementation Effort</div>
                  <div className="text-xs font-bold text-gray-900 capitalize">{selectedRecommendation.effort}</div>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <div className="text-xs text-gray-600 mb-1">Category</div>
                  <div className="text-xs font-bold text-gray-900 capitalize">
                    {selectedRecommendation.category.replace(/_/g, ' ')}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-xs"
              >
                Close
              </button>
              <div className="flex items-center space-x-3">
                {selectedRecommendation.status === 'pending' && (
                  <>
                    <button
                      onClick={() => { handleReject(selectedRecommendation.id); setShowDetailsModal(false); }}
                      className="px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 transition-colors text-xs"
                    >
                      <FontAwesomeIcon icon={faTimesCircle} className="mr-2" />
                      Reject
                    </button>
                    <button
                      onClick={() => { handleApprove(selectedRecommendation.id); setShowDetailsModal(false); }}
                      className="px-4 py-2 bg-green-50 text-green-600 border border-green-200 rounded-lg hover:bg-green-100 transition-colors text-xs"
                    >
                      <FontAwesomeIcon icon={faCheckCircle} className="mr-2" />
                      Approve
                    </button>
                  </>
                )}
                {selectedRecommendation.status === 'approved' && (
                  <button
                    onClick={() => { onDeployRecommendation(selectedRecommendation.id); setShowDetailsModal(false); }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs"
                  >
                    <FontAwesomeIcon icon={faPlay} className="mr-2" />
                    Deploy Now
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Debug Modal - Detailed Scan Report */}
      {showDebugModal && scanDetails && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-xs font-bold text-white flex items-center">
                  <FontAwesomeIcon icon={faBug} className="mr-3" />
                  Detailed Scan Report
                </h2>
                <p className="text-xs text-blue-100 mt-1">Comprehensive breakdown of all AWS resources analyzed</p>
              </div>
              <button onClick={() => setShowDebugModal(false)} className="text-white hover:text-blue-200">
                <FontAwesomeIcon icon={faTimes} className="text-xl" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-xs font-semibold text-gray-900 mb-3 flex items-center">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-blue-600 mr-2" />
                  Scan Summary
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-lg p-3 border border-blue-100">
                    <div className="text-xs text-gray-600 mb-1">Total Resources</div>
                    <div className="text-xs font-bold text-gray-900">{scanDetails.summary?.totalResources || 0}</div>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-blue-100">
                    <div className="text-xs text-gray-600 mb-1">Usage Types</div>
                    <div className="text-xs font-bold text-gray-900">{scanDetails.summary?.totalUsageTypes || 0}</div>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-blue-100">
                    <div className="text-xs text-gray-600 mb-1">Current Month Cost</div>
                    <div className="text-xs font-bold text-green-600">
                      ${scanDetails.summary?.currentMonthCost?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-blue-100">
                    <div className="text-xs text-gray-600 mb-1">Scan Duration</div>
                    <div className="text-xs font-bold text-gray-900">{scanDetails.duration || 'N/A'}</div>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleSection('resources')}
                  className="w-full px-4 py-3 bg-gray-50 flex items-center justify-between hover:bg-gray-100 transition-colors"
                >
                  <h3 className="text-xs font-semibold text-gray-900 flex items-center">
                    <FontAwesomeIcon icon={faServer} className="text-gray-600 mr-2" />
                    Resources by Category ({scanDetails.summary?.totalResources || 0} total)
                  </h3>
                  <FontAwesomeIcon
                    icon={expandedSections['resources'] ? faChevronUp : faChevronDown}
                    className="text-gray-400"
                  />
                </button>
                {expandedSections['resources'] && (
                  <div className="p-4 space-y-3">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {Object.entries(scanDetails.summary?.byCategory || {}).map(([category, count]) => (
                        <div key={category} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                          <div className="text-xs text-gray-600 capitalize">{category}</div>
                          <div className="text-xs font-bold text-gray-900">{count as number}</div>
                        </div>
                      ))}
                    </div>

                    {/* Detailed Resource Lists */}
                    <div className="mt-4 space-y-3">
                      {/* EC2 Instances */}
                      {scanDetails.resourceBreakdown?.compute?.ec2?.count > 0 && (
                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                          <h4 className="text-xs font-semibold text-orange-900 mb-2">
                            EC2 Instances ({scanDetails.resourceBreakdown.compute.ec2.count})
                          </h4>
                          <div className="text-xs text-gray-700 space-y-1 max-h-40 overflow-y-auto">
                            {scanDetails.resourceBreakdown.compute.ec2.resources.slice(0, 10).map((r: any, i: number) => (
                              <div key={i} className="flex items-center justify-between py-1 border-b border-orange-100">
                                <span className="font-mono">{r.id}</span>
                                <span className="text-orange-700">{r.instanceType} | {r.state}</span>
                              </div>
                            ))}
                            {scanDetails.resourceBreakdown.compute.ec2.count > 10 && (
                              <div className="text-xs text-orange-600 pt-2">
                                ...and {scanDetails.resourceBreakdown.compute.ec2.count - 10} more
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* EBS Volumes */}
                      {scanDetails.resourceBreakdown?.storage?.ebs?.count > 0 && (
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                          <h4 className="text-xs font-semibold text-purple-900 mb-2">
                            EBS Volumes ({scanDetails.resourceBreakdown.storage.ebs.count}) -
                            {scanDetails.resourceBreakdown.storage.ebs.totalSizeGB} GB total
                          </h4>
                          <div className="text-xs text-gray-700">
                            First 5 volumes listed (view full list in export)
                          </div>
                        </div>
                      )}

                      {/* Lambda Functions */}
                      {scanDetails.resourceBreakdown?.compute?.lambda?.count > 0 && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                          <h4 className="text-xs font-semibold text-yellow-900 mb-2">
                            Lambda Functions ({scanDetails.resourceBreakdown.compute.lambda.count})
                          </h4>
                        </div>
                      )}

                      {/* RDS Databases */}
                      {scanDetails.resourceBreakdown?.database?.rds?.count > 0 && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <h4 className="text-xs font-semibold text-blue-900 mb-2">
                            RDS Instances ({scanDetails.resourceBreakdown.database.rds.count})
                          </h4>
                          <div className="text-xs text-gray-700 space-y-1">
                            {scanDetails.resourceBreakdown.database.rds.resources.map((r: any, i: number) => (
                              <div key={i} className="flex items-center justify-between py-1 border-b border-blue-100">
                                <span className="font-mono">{r.name}</span>
                                <span className="text-blue-700">{r.engine} | {r.instanceClass}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Usage Types from Cost Explorer */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleSection('usageTypes')}
                  className="w-full px-4 py-3 bg-gray-50 flex items-center justify-between hover:bg-gray-100 transition-colors"
                >
                  <h3 className="text-xs font-semibold text-gray-900 flex items-center">
                    <FontAwesomeIcon icon={faDollarSign} className="text-green-600 mr-2" />
                    Cost Explorer Usage Types ({scanDetails.usageTypeBreakdown?.totalUsageTypes || 0} types)
                  </h3>
                  <FontAwesomeIcon
                    icon={expandedSections['usageTypes'] ? faChevronUp : faChevronDown}
                    className="text-gray-400"
                  />
                </button>
                {expandedSections['usageTypes'] && (
                  <div className="p-4 space-y-3">
                    {/* By Service Summary */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {Object.entries(scanDetails.usageTypeBreakdown?.byService || {}).map(([service, data]: [string, any]) => (
                        <div key={service} className="bg-green-50 rounded-lg p-3 border border-green-200">
                          <div className="text-xs text-gray-600 font-semibold">{service}</div>
                          <div className="text-xs font-bold text-gray-900">{data.count} types</div>
                          <div className="text-xs text-green-700">${data.totalCost?.toFixed(2) || 0}</div>
                        </div>
                      ))}
                    </div>

                    {/* Top 20 Usage Types */}
                    <div className="mt-4">
                      <h4 className="text-xs font-semibold text-gray-800 mb-2">Top 20 Usage Types by Cost</h4>
                      <div className="bg-gray-50 rounded-lg border border-gray-200 max-h-60 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-gray-100 sticky top-0">
                            <tr>
                              <th className="text-left p-2 font-semibold text-gray-700">#</th>
                              <th className="text-left p-2 font-semibold text-gray-700">Usage Type</th>
                              <th className="text-right p-2 font-semibold text-gray-700">Cost (USD)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {scanDetails.usageTypeBreakdown?.topUsageTypes?.map((item: any, i: number) => (
                              <tr key={i} className="border-b border-gray-200 hover:bg-gray-100">
                                <td className="p-2 text-gray-600">{i + 1}</td>
                                <td className="p-2 font-mono text-gray-800">{item.usageType}</td>
                                <td className="p-2 text-right font-semibold text-green-600">
                                  ${item.cost?.toFixed(2) || 0}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Cost Breakdown */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleSection('costs')}
                  className="w-full px-4 py-3 bg-gray-50 flex items-center justify-between hover:bg-gray-100 transition-colors"
                >
                  <h3 className="text-xs font-semibold text-gray-900 flex items-center">
                    <FontAwesomeIcon icon={faDollarSign} className="text-green-600 mr-2" />
                    Cost Breakdown & Coverage Metrics
                  </h3>
                  <FontAwesomeIcon
                    icon={expandedSections['costs'] ? faChevronUp : faChevronDown}
                    className="text-gray-400"
                  />
                </button>
                {expandedSections['costs'] && (
                  <div className="p-4 space-y-4">
                    {/* Monthly Costs */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                        <div className="text-xs text-gray-600">Current Month (Net Amortized)</div>
                        <div className="text-xs font-bold text-green-700">
                          ${scanDetails.costBreakdown?.currentMonth?.netAmortizedCost?.toFixed(2) || 0}
                        </div>
                      </div>
                      <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                        <div className="text-xs text-gray-600">Previous Month</div>
                        <div className="text-xs font-bold text-blue-700">
                          ${scanDetails.costBreakdown?.previousMonth?.netAmortizedCost?.toFixed(2) || 0}
                        </div>
                      </div>
                    </div>

                    {/* Coverage Metrics */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                        <div className="text-xs text-gray-600">RI Coverage</div>
                        <div className="text-xs font-bold text-purple-700">
                          {scanDetails.coverageMetrics?.reservedInstances?.coverage?.toFixed(1) || 0}%
                        </div>
                      </div>
                      <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                        <div className="text-xs text-gray-600">RI Utilization</div>
                        <div className="text-xs font-bold text-purple-700">
                          {scanDetails.coverageMetrics?.reservedInstances?.utilization?.toFixed(1) || 0}%
                        </div>
                      </div>
                      <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-200">
                        <div className="text-xs text-gray-600">SP Coverage</div>
                        <div className="text-xs font-bold text-indigo-700">
                          {scanDetails.coverageMetrics?.savingsPlans?.coverage?.toFixed(1) || 0}%
                        </div>
                      </div>
                      <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-200">
                        <div className="text-xs text-gray-600">SP Utilization</div>
                        <div className="text-xs font-bold text-indigo-700">
                          {scanDetails.coverageMetrics?.savingsPlans?.utilization?.toFixed(1) || 0}%
                        </div>
                      </div>
                    </div>

                    {/* Compute Costs by Pricing Model */}
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <h4 className="text-xs font-semibold text-gray-800 mb-2">Compute Costs by Pricing Model</h4>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                        <div>
                          <span className="text-gray-600">On-Demand:</span>
                          <span className="ml-1 font-semibold text-gray-900">
                            ${scanDetails.coverageMetrics?.computeCosts?.onDemand?.toFixed(2) || 0}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Spot:</span>
                          <span className="ml-1 font-semibold text-gray-900">
                            ${scanDetails.coverageMetrics?.computeCosts?.spot?.toFixed(2) || 0}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Reserved:</span>
                          <span className="ml-1 font-semibold text-gray-900">
                            ${scanDetails.coverageMetrics?.computeCosts?.reserved?.toFixed(2) || 0}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">Savings Plan:</span>
                          <span className="ml-1 font-semibold text-gray-900">
                            ${scanDetails.coverageMetrics?.computeCosts?.savingsPlan?.toFixed(2) || 0}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600 font-bold">Total:</span>
                          <span className="ml-1 font-bold text-green-700">
                            ${scanDetails.coverageMetrics?.computeCosts?.total?.toFixed(2) || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleSection('log')}
                  className="w-full px-4 py-3 bg-gray-50 flex items-center justify-between hover:bg-gray-100 transition-colors"
                >
                  <h3 className="text-xs font-semibold text-gray-900 flex items-center">
                    <FontAwesomeIcon icon={faClock} className="text-gray-600 mr-2" />
                    Scan Progress Log ({scanDetails.progressLog?.length || 0} entries)
                  </h3>
                  <FontAwesomeIcon
                    icon={expandedSections['log'] ? faChevronUp : faChevronDown}
                    className="text-gray-400"
                  />
                </button>
                {expandedSections['log'] && (
                  <div className="p-4">
                    <div className="bg-gray-900 rounded-lg p-3 max-h-60 overflow-y-auto font-mono text-xs text-green-400">
                      {scanDetails.progressLog?.map((entry: string, i: number) => (
                        <div key={i} className="py-1">{entry}</div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-xs text-gray-600">
                Scan ID: <span className="font-mono">{scanDetails.scanId}</span>
              </div>
              <button
                onClick={() => setShowDebugModal(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};